// AiFeedbackService.js — submit / list Ai reply feedback

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';
import {
  AI_FEEDBACK_STATUS,
  normalizeAiFeedbackStatus,
} from 'spaces/utils/aiFeedbackUtils';

const FEEDBACK_COLLECTION = collections.aiFeedback;
const LIST_LIMIT = 100;

function cleanString(value) {
  return String(value || '').trim();
}

function buildFeedbackDocId(messageKey, userKey) {
  return `${cleanString(messageKey)}__${cleanString(userKey)}`;
}

function mapFeedbackDoc(entry) {
  const data = entry.data() || {};
  return {
    key: entry.id,
    ...data,
    status: normalizeAiFeedbackStatus(data.status),
    adminComment: cleanString(data.adminComment),
  };
}

/**
 * Upserts one feedback vote per user per AI message.
 */
export async function submitAiFeedback({
  messageKey,
  baseKey,
  channelKey,
  messageType = '',
  vote,
  comment = '',
  promptText = '',
  replyText = '',
  userKey,
  userName = '',
} = {}) {
  const cleanMessageKey = cleanString(messageKey);
  const cleanUserKey = cleanString(userKey);
  const cleanVote = cleanString(vote);
  const cleanBaseKey = cleanString(baseKey);
  const cleanChannelKey = cleanString(channelKey);

  if (!cleanMessageKey || !cleanUserKey) {
    throw new Error('messageKey and userKey are required.');
  }
  if (cleanVote !== 'up' && cleanVote !== 'down') {
    throw new Error('vote must be "up" or "down".');
  }
  if (!cleanBaseKey || !cleanChannelKey) {
    throw new Error('baseKey and channelKey are required.');
  }

  const feedbackId = buildFeedbackDocId(cleanMessageKey, cleanUserKey);
  const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);
  const existing = await getDoc(feedbackRef);

  const payload = {
    key: feedbackId,
    vote: cleanVote,
    comment: cleanString(comment),
    promptText: cleanString(promptText),
    replyText: cleanString(replyText),
    messageKey: cleanMessageKey,
    baseKey: cleanBaseKey,
    channelKey: cleanChannelKey,
    messageType: cleanString(messageType),
    userKey: cleanUserKey,
    userName: cleanString(userName),
    dateModified: serverTimestamp(),
  };

  if (!existing.exists()) {
    payload.dateCreated = serverTimestamp();
    payload.status = AI_FEEDBACK_STATUS.NEW;
  }

  await setDoc(feedbackRef, payload, { merge: true });
  return { key: feedbackId, ...payload };
}

/**
 * Lists recent AI feedback for the admin Prompts tab (superuser rules).
 */
export async function listAiFeedback({ max = LIST_LIMIT } = {}) {
  const feedbackQuery = query(
    collection(db, FEEDBACK_COLLECTION),
    orderBy('dateCreated', 'desc'),
    limit(Math.max(1, Math.min(Number(max) || LIST_LIMIT, 500))),
  );
  const snapshot = await getDocs(feedbackQuery);
  return snapshot.docs.map(mapFeedbackDoc);
}

/**
 * Lists the signed-in user's AI feedback for Account → My Feedback.
 */
export async function listAiFeedbackByUser({
  userKey,
  max = LIST_LIMIT,
} = {}) {
  const cleanUserKey = cleanString(userKey);
  if (!cleanUserKey) {
    throw new Error('userKey is required.');
  }

  const feedbackQuery = query(
    collection(db, FEEDBACK_COLLECTION),
    where('userKey', '==', cleanUserKey),
    orderBy('dateCreated', 'desc'),
    limit(Math.max(1, Math.min(Number(max) || LIST_LIMIT, 500))),
  );
  const snapshot = await getDocs(feedbackQuery);
  return snapshot.docs.map(mapFeedbackDoc);
}

/**
 * Updates admin review fields on one feedback doc (superuser rules).
 */
export async function updateAiFeedbackAdminReview({
  feedbackKey,
  status,
  adminComment = '',
} = {}) {
  const cleanKey = cleanString(feedbackKey);
  if (!cleanKey) throw new Error('feedbackKey is required.');

  const nextStatus = normalizeAiFeedbackStatus(status);
  await updateDoc(doc(db, FEEDBACK_COLLECTION, cleanKey), {
    status: nextStatus,
    adminComment: cleanString(adminComment),
    dateModified: serverTimestamp(),
  });

  return {
    key: cleanKey,
    status: nextStatus,
    adminComment: cleanString(adminComment),
  };
}

/**
 * Deletes one feedback doc (admin Prompts tab; superuser rules).
 */
export async function deleteAiFeedback(feedbackKey) {
  const cleanKey = cleanString(feedbackKey);
  if (!cleanKey) throw new Error('feedbackKey is required.');
  await deleteDoc(doc(db, FEEDBACK_COLLECTION, cleanKey));
  return { key: cleanKey };
}
